// frontend\src\components\analytics\AnalyticsReports.tsx
import React, { useState } from 'react';
import { PopularProduct, TopCustomer, AnalyticsCompareMode } from '../../types';
import { getComparisonRange } from '../../utils/comparisonRange';

interface AnalyticsReportsProps {
  popularProducts: PopularProduct[] | null;
  topCustomers: TopCustomer[] | null;
  loadingProducts: boolean;
  loadingCustomers: boolean;
  fetchPopularProducts: () => void;
  fetchTopCustomers: () => void;
  setPopularProducts: (products: PopularProduct[] | null) => void;
  setTopCustomers: (customers: TopCustomer[] | null) => void;
  startDate: string;
  endDate: string;
  compareMode: AnalyticsCompareMode;
  search: string;
  selectedTag: string | null;
  selectedCustomerName: string | null;
}

const AnalyticsReports: React.FC<AnalyticsReportsProps> = ({
  popularProducts,
  topCustomers,
  loadingProducts,
  loadingCustomers,
  fetchPopularProducts,
  fetchTopCustomers,
  setPopularProducts,
  setTopCustomers,
  startDate,
  endDate,
  compareMode,
  search,
  selectedTag,
  selectedCustomerName,
}) => {
  type SortColumn = 'name' | 'revenue' | 'num_sold' | 'num_sold_compare' | 'percent_change';
  const [sortColumn, setSortColumn] = useState<SortColumn>('revenue');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const formatDateRange = (start: string, end: string) => {
    if (!start || !end) return '';
    const parseDate = (dateStr: string) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
    const startFormatted = parseDate(start).toLocaleDateString('en-US', options);
    const endFormatted = parseDate(end).toLocaleDateString('en-US', options);
    return `${startFormatted} - ${endFormatted}`;
  };

  const getCompareDateRange = () => {
    if (!startDate || !endDate) return '';
    // Mirrors api/utils/comparison-range.ts; these reports only ever show the nearest period.
    const range = getComparisonRange(startDate, endDate, compareMode, 1);
    return formatDateRange(range.startDate, range.endDate);
  };

  const renderTable = (data: PopularProduct[] | TopCustomer[], type: 'products' | 'customers') => {
    const sortedData = [...data].sort((a, b) => {
      let aValue: string | number | null = null;
      let bValue: string | number | null = null;
      if (sortColumn === 'name') {
        aValue = type === 'products' ? (a as PopularProduct).product_name.toLowerCase() : (a as TopCustomer).customer_name.toLowerCase();
        bValue = type === 'products' ? (b as PopularProduct).product_name.toLowerCase() : (b as TopCustomer).customer_name.toLowerCase();
      } else if (sortColumn === 'percent_change') {
        aValue = a.percent_change ?? Infinity;
        bValue = b.percent_change ?? Infinity;
      } else {
        aValue = a[sortColumn];
        bValue = b[sortColumn];
      }
      let comparison = 0;
      if (aValue === Infinity && bValue === Infinity) {
        comparison = 0;
      } else if (aValue === Infinity) {
        comparison = 1;
      } else if (bValue === Infinity) {
        comparison = -1;
      } else if (aValue == null && bValue == null) {
        comparison = 0;
      } else if (aValue == null) {
        comparison = -1;
      } else if (bValue == null) {
        comparison = 1;
      } else if (aValue < bValue) {
        comparison = -1;
      } else if (aValue > bValue) {
        comparison = 1;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    const totals = (data as Array<{ revenue: number; num_sold: number; num_sold_compare: number }>).reduce(
      (acc, item) => ({
        revenue: acc.revenue + item.revenue,
        num_sold: acc.num_sold + item.num_sold,
        num_sold_compare: acc.num_sold_compare + item.num_sold_compare,
      }),
      { revenue: 0, num_sold: 0, num_sold_compare: 0 }
    );

    return (
      <>
        <div className="analytics-report-meta">
          <h4>{type === 'products' ? 'Most Popular Products' : 'Top Customers'}</h4>
          <p>
            {search || selectedTag || selectedCustomerName
              ? `Filtering only ${
                  selectedTag
                    ? `${selectedTag} (tag)`
                    : selectedCustomerName
                    ? `${selectedCustomerName} (customer)`
                    : search
                }`
              : 'No filters applied'}
          </p>
          <p>Sales From {formatDateRange(startDate, endDate)}</p>
          <p>Comparing to {getCompareDateRange()}</p>
        </div>
        <table className="analytics-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')}>
                {type === 'products' ? 'Product' : 'Customer'}{' '}
                {sortColumn === 'name' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
              </th>
              <th onClick={() => handleSort('revenue')}>
                Revenue{' '}
                {sortColumn === 'revenue' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
              </th>
              <th style={{ textAlign: 'center' }} onClick={() => handleSort('num_sold')}>
                Items Sold{' '}
                {sortColumn === 'num_sold' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
              </th>
              <th style={{ textAlign: 'center' }} onClick={() => handleSort('num_sold_compare')}>
                Previous Period{' '}
                {sortColumn === 'num_sold_compare' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th style={{ textAlign: 'center' }} onClick={() => handleSort('percent_change')}>
                % Change{' '}
                {sortColumn === 'percent_change' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item) => (
              <tr key={type === 'products' ? (item as PopularProduct).sku : (item as TopCustomer).customer_name}>
                <td>
                  {type === 'products'
                    ? `${(item as PopularProduct).product_name} (${(item as PopularProduct).sku})`
                    : (item as TopCustomer).customer_name}
                </td>
                <td>
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  }).format(item.revenue)}
                </td>
                <td style={{ textAlign: 'center' }}>{item.num_sold}</td>
                <td style={{ textAlign: 'center' }}>{item.num_sold_compare}</td>
                <td style={{ textAlign: 'center' }}>
                  {item.percent_change !== null ? (
                    <span
                      className={`analytics-percentage-change ${item.percent_change < 0 ? 'negative' : ''}`}
                      style={{ fontSize: '1rem' }}
                    >
                      {item.percent_change >= 0 ? '↑' : '↓'} {Math.abs(item.percent_change).toFixed(1)}%
                    </span>
                  ) : (
                    <span style={{ fontSize: '1.2rem' }}>∞</span>
                  )}
                </td>
              </tr>
            ))}
            {totals && (
              <tr className="analytics-table-total-row">
                <td>Total</td>
                <td>
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  }).format(totals.revenue)}
                </td>
                <td style={{ textAlign: 'center' }}>{totals.num_sold}</td>
                <td style={{ textAlign: 'center' }}>{totals.num_sold_compare}</td>
                <td style={{ textAlign: 'center' }}>-</td>
              </tr>
            )}
          </tbody>
        </table>
        <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
          <button
            className="analytics-action-button"
            onClick={() => {
              setPopularProducts(null);
              setTopCustomers(null);
            }}
          >
            Back to reports
          </button>
          <button
            className="analytics-action-button"
            onClick={() => {
              const csvRows = [
                [
                  type === 'products' ? 'Product' : 'Customer',
                  type === 'products' ? 'SKU' : '',
                  'Revenue',
                  'Items Sold',
                  'Previous Period',
                  '% Change',
                ].join(','),
                ...sortedData.map((item) => [
                  type === 'products'
                    ? `"${(item as PopularProduct).product_name.replace(/"/g, '""')}"`
                    : `"${(item as TopCustomer).customer_name.replace(/"/g, '""')}"`,
                  type === 'products' ? (item as PopularProduct).sku : '',
                  item.revenue.toFixed(2),
                  item.num_sold,
                  item.num_sold_compare,
                  item.percent_change !== null ? item.percent_change.toFixed(1) + '%' : '∞',
                ].join(',')),
                [
                  'Total',
                  '',
                  totals?.revenue.toFixed(2),
                  totals?.num_sold,
                  totals?.num_sold_compare,
                  '-',
                ].join(','),
              ];
              const csvContent = csvRows.join('\n');
              const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.setAttribute('href', url);
              link.setAttribute('download', `${type === 'products' ? 'product_popularity' : 'top_customers'}_report.csv`);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }}
          >
            Export to CSV
          </button>
        </div>
      </>
    );
  };

  if (loadingProducts || loadingCustomers) {
    return <div className="analytics-container">Loading reports...</div>;
  }

  return (
    <div className="analytics-card analytics-reports-card">
      <div className="analytics-card-header">
        <h3>Reports</h3>
      </div>
      {!popularProducts && !topCustomers ? (
        <div style={{ display: 'flex', gap: '16px' }}>
          <button
            className="analytics-action-button"
            onClick={fetchPopularProducts}
            disabled={loadingProducts}
          >
            Generate Product Popularity Report
          </button>
          <button
            className="analytics-action-button"
            onClick={fetchTopCustomers}
            disabled={loadingCustomers}
          >
            Generate Top Customers Report
          </button>
        </div>
      ) : popularProducts ? (
        renderTable(popularProducts, 'products')
      ) : (
        renderTable(topCustomers!, 'customers')
      )}
    </div>
  );
};

export default AnalyticsReports;